/**
 * history-store — local chat-history persistence for the Usable Chat embed.
 *
 * HOST-AGNOSTIC: NO Office.js imports. Stores plain JSON (`ExportedMessage` from
 * the embed contract) scoped by `userId` (the JWT `sub`, supplied by the caller —
 * this file never reads auth itself).
 *
 * Multi-app: `createChatHistoryStore({ dbName })` returns an isolated store bound
 * to one IndexedDB database, so each add-in gets its own history
 * (`usable-excel-chat-history`, `usable-word-chat-history`, …).
 *
 * Idempotency: every write is a `put` keyed on a stable id (`message.id` for
 * messages, `conversationId` for conversations). The Office/WKWebView + legacy
 * React environment can deliver the same lifecycle event twice; put-by-id means a
 * duplicate delivery overwrites the same row — never a dupe.
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { ExportedMessage } from "./embed-sdk";

export interface ConversationRecord {
  id: string;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export interface MessageRecord {
  /** == ExportedMessage.id — the idempotency key. */
  id: string;
  conversationId: string;
  userId: string;
  kind: "user" | "assistant";
  message: ExportedMessage;
  createdAt: string;
}

interface MetaRecord {
  userId: string;
  lastActiveConversationId: string | null;
}

interface ChatHistoryDB extends DBSchema {
  conversations: {
    key: string;
    value: ConversationRecord;
    indexes: { "by-user": string };
  };
  messages: {
    key: string;
    value: MessageRecord;
    indexes: { "by-conversation": string };
  };
  meta: {
    key: string;
    value: MetaRecord;
  };
}

const DB_VERSION = 1;

/** Placeholder title for a conversation the embed created without naming. */
export const DEFAULT_CONVERSATION_TITLE = "Embed Session";

export interface UpsertConversationInput {
  id: string;
  userId: string;
  title?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AppendMessageInput {
  userId: string;
  conversationId: string;
  kind: "user" | "assistant";
  message: ExportedMessage;
}

/** Host-agnostic local chat-history store, bound to a single IndexedDB database. */
export interface ChatHistoryStore {
  upsertConversation(input: UpsertConversationInput): Promise<void>;
  appendMessage(input: AppendMessageInput): Promise<{ inserted: boolean }>;
  renameConversation(userId: string, conversationId: string, title: string): Promise<void>;
  setLastActive(userId: string, conversationId: string | null): Promise<void>;
  deleteConversation(userId: string, conversationId: string): Promise<void>;
  getLastActive(userId: string): Promise<string | null>;
  loadConversation(
    conversationId: string
  ): Promise<{ conversation: ConversationRecord | undefined; messages: MessageRecord[] }>;
  listConversations(userId: string): Promise<ConversationRecord[]>;
  getCounts(userId: string): Promise<{ conversations: number; messages: number }>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function isPlaceholderTitle(title: string): boolean {
  return !title || title === DEFAULT_CONVERSATION_TITLE;
}

function deriveTitleFromContent(content: unknown): string | null {
  if (typeof content !== "string") return null;
  const text = content.replace(/\s+/g, " ").trim();
  if (!text) return null;
  return text.length > 60 ? `${text.slice(0, 57).trimEnd()}…` : text;
}

/**
 * Create a local chat-history store for one add-in.
 * @param dbName IndexedDB database name, e.g. `usable-excel-chat-history`.
 */
export function createChatHistoryStore({ dbName }: { dbName: string }): ChatHistoryStore {
  let dbPromise: Promise<IDBPDatabase<ChatHistoryDB>> | null = null;

  function getDB(): Promise<IDBPDatabase<ChatHistoryDB>> {
    if (!dbPromise) {
      dbPromise = openDB<ChatHistoryDB>(dbName, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains("conversations")) {
            const conv = db.createObjectStore("conversations", { keyPath: "id" });
            conv.createIndex("by-user", "userId");
          }
          if (!db.objectStoreNames.contains("messages")) {
            const msg = db.createObjectStore("messages", { keyPath: "id" });
            msg.createIndex("by-conversation", "conversationId");
          }
          if (!db.objectStoreNames.contains("meta")) {
            db.createObjectStore("meta", { keyPath: "userId" });
          }
        },
      });
    }
    return dbPromise;
  }

  return {
    async upsertConversation(input) {
      const db = await getDB();
      const existing = await db.get("conversations", input.id);
      const record: ConversationRecord = {
        id: input.id,
        userId: input.userId,
        title: input.title ?? existing?.title ?? DEFAULT_CONVERSATION_TITLE,
        createdAt: existing?.createdAt ?? input.createdAt ?? nowIso(),
        updatedAt: input.updatedAt ?? nowIso(),
      };
      await db.put("conversations", record);
    },

    async appendMessage(input) {
      const db = await getDB();
      const createdAt = input.message.createdAt ?? nowIso();

      const tx = db.transaction(["messages", "conversations"], "readwrite");
      const messages = tx.objectStore("messages");
      const conversations = tx.objectStore("conversations");

      const existing = await messages.get(input.message.id);
      const inserted = existing === undefined;

      const record: MessageRecord = {
        id: input.message.id,
        conversationId: input.conversationId,
        userId: input.userId,
        kind: input.kind,
        message: input.message,
        createdAt,
      };
      await messages.put(record);

      const conv = await conversations.get(input.conversationId);
      const bumped = nowIso();
      if (conv) {
        conv.updatedAt = bumped;
        await conversations.put(conv);
      } else {
        // Stub: message arrived before CONVERSATION_CREATED (or it was missed).
        await conversations.put({
          id: input.conversationId,
          userId: input.userId,
          title: DEFAULT_CONVERSATION_TITLE,
          createdAt,
          updatedAt: bumped,
        });
      }

      await tx.done;
      return { inserted };
    },

    async renameConversation(_userId, conversationId, title) {
      const db = await getDB();
      const conv = await db.get("conversations", conversationId);
      if (conv) {
        conv.title = title;
        conv.updatedAt = nowIso();
        await db.put("conversations", conv);
      }
    },

    async setLastActive(userId, conversationId) {
      const db = await getDB();
      await db.put("meta", { userId, lastActiveConversationId: conversationId });
    },

    async deleteConversation(userId, conversationId) {
      const db = await getDB();
      const tx = db.transaction(["conversations", "messages", "meta"], "readwrite");

      let cursor = await tx.objectStore("messages").index("by-conversation").openCursor(conversationId);
      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }

      await tx.objectStore("conversations").delete(conversationId);

      const meta = await tx.objectStore("meta").get(userId);
      if (meta && meta.lastActiveConversationId === conversationId) {
        await tx.objectStore("meta").put({ userId, lastActiveConversationId: null });
      }

      await tx.done;
    },

    async getLastActive(userId) {
      const db = await getDB();
      const meta = await db.get("meta", userId);
      return meta?.lastActiveConversationId ?? null;
    },

    async loadConversation(conversationId) {
      const db = await getDB();
      const conversation = await db.get("conversations", conversationId);
      const messages = await db.getAllFromIndex("messages", "by-conversation", conversationId);
      messages.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
      return { conversation, messages };
    },

    async listConversations(userId) {
      const db = await getDB();
      const all = await db.getAllFromIndex("conversations", "by-user", userId);
      all.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));

      for (const conv of all) {
        if (!isPlaceholderTitle(conv.title)) continue;
        const messages = await db.getAllFromIndex("messages", "by-conversation", conv.id);
        messages.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));
        const firstUser = messages.find((m) => m.kind === "user");
        const derived = deriveTitleFromContent(firstUser?.message.content);
        if (derived) conv.title = derived;
      }

      return all;
    },

    async getCounts(userId) {
      const db = await getDB();
      const conversations = await db.getAllFromIndex("conversations", "by-user", userId);
      let messages = 0;
      for (const conv of conversations) {
        messages += await db.countFromIndex("messages", "by-conversation", conv.id);
      }
      return { conversations: conversations.length, messages };
    },
  };
}
